import React, { useMemo } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { IconButton, Tooltip, Chip } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import ColumnSelector from "./ColumnSelector";
import { Box } from "@mui/system";

const PatientMapRosterTable = ({
  data = [],
  loading,
  total,
  page,
  pageSize,
  setPage,
  setPageSize,
  onSelectPatient
}) => {
  // Filtrar por searchTerm en cliente
  const rows = useMemo(() => {
  return data.map(p => ({ ...p, id: p.cipId }));
}, [data]);


  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({
    memberType: false,
    locationAddress: false,
    age: false,
  });
  // Columnas sin capacidad de filtrado en columna
  const columns = useMemo(() => [
    { field: "memberType",      headerName: "Type",            flex: 1, filterable: false },
    { field: "memberId",        headerName: "Member ID",       flex: 1, filterable: false },
    { field: "memberFullName",  headerName: "Name",            flex: 2, filterable: false },
    {
      field: "status",
      headerName: "Status",
      flex: 0.8,
      filterable: false,
      renderCell: (params) => {
        const isInactive = params.value === 1;
        if (isInactive) {
          return (
            <Chip
              size="small"
              icon={<PersonOffOutlinedIcon />}
              label="Inactive"
              color="error"
              variant="outlined"
              sx={{ borderRadius: 2 }}
            />
          );
        }
        return (
          <Chip
            size="small"
            label="Active"
            color="success"
            variant="outlined"
            sx={{ borderRadius: 2 }}
          />
        );
      }
    },
    {
      field: "dateOfBirth",
      headerName: "Date of Birth",
      flex: 1,
      filterable: false,
      valueFormatter: (params) => {
        if (!params) return "—";
        const d = new Date(params);
        if (isNaN(d.getTime())) return String(params);
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        return `${yyyy}-${mm}-${dd}`;
      }
    },
    { field: "age",             headerName: "Age",             flex: 0.5, filterable: false },
    { field: "ageRange",        headerName: "Age Range",       flex: 1, filterable: false },
    { field: "gender",          headerName: "Gender",          flex: 0.5, filterable: false },
    { field: "pcpName",         headerName: "PCP",             flex: 1.5, filterable: false },
    { field: "pcpTitle",        headerName: "Title",           flex: 1, filterable: false },
    { field: "medicalCenter",   headerName: "Medical Center",  flex: 1.5, filterable: false },
    { field: "locationAddress", headerName: "Address",         flex: 2, filterable: false },
    { field: "carePlansCount",  headerName: "Care Plans",      flex: 1, filterable: false },
    {
      field: "isAdmitted",
      headerName: "Admitted",
      flex: 0.8,
      filterable: false,
      valueFormatter: params => (params == 1 ? "Yes" : "No")
    },
    {
      field: "isDischarged",
      headerName: "Discharged",
      flex: 0.8,
      filterable: false,
      valueFormatter: params => (params == 1 ? "Yes" : "No")
    },
    {
      field: "isEmergency",
      headerName: "Emergency",
      flex: 0.8,
      filterable: false,
      valueFormatter: params => (params == 1 ? "Yes" : "No")
    },
    {
      field: "isHighCost",
      headerName: "High Cost",
      flex: 0.8,
      filterable: false,
      valueFormatter: params => (params == 1 ? "Yes" : "No")
    },
    {
      field: "isHighRisk",
      headerName: "High Risk",
      flex: 0.8,
      filterable: false,
      valueFormatter: params => (params == 1 ? "Yes" : "No")
    },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      filterable: false,
      headerAlign: "center",
      align: "center",
      flex: 0.8,
      renderCell: params => (
        <Tooltip title="View patient summary">
          <IconButton onClick={() => onSelectPatient(params.row)} color="primary">
            <VisibilityIcon />
          </IconButton>
        </Tooltip>
      )
    }
  ], [onSelectPatient]);

  return (
    <div style={{ height: 700, width: "100%" }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <ColumnSelector
          columns={columns}
          columnVisibilityModel={columnVisibilityModel}
          setColumnVisibilityModel={setColumnVisibilityModel}
        />
      </Box>
      <DataGrid
        rows={rows}
        columns={columns}
        rowCount={total}
        loading={loading}
        paginationMode="server"
        paginationModel={{ page, pageSize }}
        onPaginationModelChange={({ page, pageSize }) => {
          setPage(page);
          setPageSize(pageSize);
        }}
        rowsPerPageOptions={[10, 25, 50, 100]}
        sx={{
          border: '1px solid #ccc',
          '& .MuiDataGrid-footerContainer': {
            justifyContent: 'flex-start',
          },
          '& .MuiDataGrid-columnHeader': {
            backgroundColor: '#607d8b',
            color: '#fff',
            fontWeight: 'bold',
          },
          '& .MuiDataGrid-row': {
            backgroundColor: '#fff', 
          },
          '& .MuiDataGrid-row:nth-of-type(even)': {
            backgroundColor: '#f5f5f5', 
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: '#e3f2fd',
          },
          '& .MuiDataGrid-cell': {
            borderBottom: '1px solid #eee',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          },
        }}
        density="compact"
        disableColumnFilter
        disableColumnSelector
        columnVisibilityModel={columnVisibilityModel}
        setColumnVisibilityModel={()=>setColumnVisibilityModel()}
      />
    </div>
  );
};

export default PatientMapRosterTable;
